'use client';

import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from 'react';
import { faColumns3 } from '@fortawesome/pro-solid-svg-icons';
import { ColumnMenuItem } from './components';
import { VOLUME_SELECT_OPTIONS } from 'src/components/constants';
import { PulseButton } from 'src/components';

interface TableColumnMenuProps {
  columns?: any[];
  columnVisibilityModel?: any;
  setColumnVisibilityModel?: any;
  table?: string;
}

const TableColumnMenu = ({
  columns = [],
  columnVisibilityModel = {},
  setColumnVisibilityModel,
  table = '',
}: TableColumnMenuProps) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Handle click outside
  const handleClickOutside = useCallback((event: MouseEvent) => {
    if (
      dropdownRef.current &&
      !dropdownRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    ) {
      setIsMenuOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
      }
    },
    [isMenuOpen]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  const handleChange = useCallback(
    (field: string) => {
      setColumnVisibilityModel?.((prev: any) => ({
        ...prev,
        [field]: !prev[field],
      }));
    },
    [setColumnVisibilityModel]
  );

  const toggleMenu = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault();
      e.stopPropagation();

      // Explicitly close if open, open if closed
      if (isMenuOpen) {
        setIsMenuOpen(false);
      } else {
        setIsMenuOpen(true);
      }
    },
    [isMenuOpen]
  );

  const visibleColumns = useMemo(() => {
    if (!isMenuOpen) return [];

    // Remove all options columns from the list
    return columns
      .filter(Boolean)
      .filter(
        (column) =>
          !VOLUME_SELECT_OPTIONS.map((option) => option.value).includes(
            column.field?.split('_')[1]
          )
      );
  }, [isMenuOpen, columns]);

  return (
    <div className="relative inline-block text-left">
      {/* Button */}
      <div ref={buttonRef}>
        <PulseButton
          size="sm"
          variant="outline"
          color="neutral"
          data-testid={`${table}-column-menu-icon-button`}
          startDecorator={faColumns3}
          onClick={toggleMenu}
          className="w-full max-w-36 h-9 rounded-full"
        >
          Columns
        </PulseButton>
      </div>

      {/* Dropdown Menu */}
      {isMenuOpen && (
        <div
          id={`${table}-column-menu`}
          data-testid={`${table}-column-menu`}
          className="absolute right-0 mt-2 w-64 rounded-md shadow-lg bg-white border border-neutral-200 z-50 max-h-80 overflow-auto"
          ref={dropdownRef}
          role="menu"
          aria-label="Column options"
        >
          <div className="py-1" role="none">
            {visibleColumns?.map((column) => (
              <ColumnMenuItem
                key={`column_${column.field}`}
                id={`${table}-column-menu-item-${column.field}`}
                field={column.field}
                headerName={column.headerName}
                isVisible={!!columnVisibilityModel[column.field]}
                onChange={handleChange}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TableColumnMenu;
